import { useMemo } from 'react'
import { useWizard } from '../../../context/WizardContext'
import { STEP } from '../../../utils/steps'
import { parseItems } from '../../../utils/items'
import { isPadala } from '../../../utils/service'
import { budgetFor } from '../../../utils/budget'
import { distanceKm, asPoint } from '../../../utils/geo'
import fareFor from '../../../utils/fare'
import Button from '../../common/Button'
import { ACTIONS, FIELDS, MESSAGES, PAYMENT_COPY, peso } from '../../../utils/copy'
import { dateTime } from '../../../utils/helpers'
import { toE164 } from '../../../utils/phone'

/**
 * Step 9 — everything read back, and the first honest price on the screen.
 *
 * **The fee is the reason this screen matters.** Until now the customer committed and
 * then found out: the fare appeared on the order detail *after* the order existed, and
 * ₱80 on a seven-hundred-metre trip is a surprise worth not having. `utils/fare.js` was
 * copied into this app for exactly this and nothing ever imported it.
 *
 * What is shown here is the **base fare** — the partner's own pricing over the pickup-to-
 * dropoff distance, computed with the same arithmetic the server uses, to the centavo. It
 * is not yet the whole fee: the rider's own trip to the shop is added when a rider is
 * chosen, and locking that in is D5's job. Until then the screen says so rather than
 * implying the number is final.
 *
 * Every row links back to the step that set it. A summary that can only be corrected by
 * pressing Back eight times is a summary people accept without reading.
 */

function Row({ label, value, onEdit, muted }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-hairline last:border-b-0">
      <span className="w-[38%] flex-none text-label font-semibold uppercase text-ink-3 pt-0.5">
        {label}
      </span>

      <span className={`min-w-0 flex-1 text-body break-words ${muted ? 'text-ink-3' : 'text-ink'}`}>
        {value}
      </span>

      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="flex-none min-h-[32px] px-2 text-body-s font-semibold text-brand
                     transition-colors duration-quick ease-out hover:text-brand-lift"
        >
          {ACTIONS.edit}
        </button>
      )}
    </div>
  )
}

export default function SummaryStep({ partner, delivery }) {
  const { state, goTo } = useWizard()

  const items = useMemo(() => parseItems(state.itemsText), [state.itemsText])
  const { estimatedTotal, maxBudget } = budgetFor(state)
  const padala = isPadala(state)

  /**
   * The base fare, computed here from the partner's own pricing.
   *
   * Still worth having with the server's quote alongside it: it renders the instant the
   * summary opens, where the quote takes a round trip and a rider search. It is what the
   * customer reads while the exact figure is still arriving, and the two agree to the
   * centavo because `utils/fare.js` and `utils/geo.js` are ports of the server's own.
   */
  const fare = useMemo(() => {
    const from = asPoint(state.pickup)
    const to = asPoint(state.dropoff)
    const rules = partner?.pricing

    if (!from || !to || !rules) return null

    return fareFor(distanceKm(from, to), rules)
  }, [state.pickup, state.dropoff, partner])

  /**
   * Read back with the country code, which the input printed beside the box rather than
   * storing. Ten bare digits on a summary is the one place they stop looking like a phone
   * number — there is no label here lending them the +63.
   */
  const contact = [state.dropoffContactName || null, toE164(state.phoneNumber)]
    .filter(Boolean)
    .join(' · ')

  const pickupContact = [state.pickupContactName || null, toE164(state.pickupContactPhone) || null]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="grid gap-4">
      <section className="border border-hairline rounded-card bg-surface px-4 py-1">
        <Row
          label="Serbisyo"
          value={padala ? 'Padala' : 'Pabili'}
          onEdit={() => goTo(STEP.SERVICE)}
        />

        <Row
          label="Kukunin sa"
          value={state.pickupSource || state.pickup.label}
          onEdit={() => goTo(STEP.PICKUP)}
        />

        <Row
          label="Kontak doon"
          value={pickupContact || 'Ikaw ang tatawagan'}
          muted={!pickupContact}
          onEdit={() => goTo(STEP.PICKUP_CONTACT)}
        />

        <Row
          label="Kailangan"
          value={
            <ol className="grid gap-0.5">
              {items.map((item, index) => (
                <li key={index}>
                  <span className="font-semibold tabular-nums text-brand">{item.quantity}</span>
                  {' '}{item.name}
                </li>
              ))}
            </ol>
          }
          onEdit={() => goTo(STEP.ITEMS)}
        />

        {!padala && (
          <Row
            label={FIELDS.estimatedTotal}
            value={`${peso(estimatedTotal)} · hanggang ${peso(maxBudget)}`}
            onEdit={() => goTo(STEP.BUDGET)}
          />
        )}

        <Row
          label="Ihahatid sa"
          value={state.dropoff.label || 'Naka-pin sa mapa'}
          onEdit={() => goTo(STEP.DROPOFF)}
        />

        <Row label="Tatawagan" value={contact} onEdit={() => goTo(STEP.DROPOFF)} />

        <Row
          label="Oras"
          value={
            state.pickupTime || state.deliveryTime
              ? [
                state.pickupTime && `Kuha: ${dateTime(state.pickupTime)}`,
                state.deliveryTime && `Hatid: ${dateTime(state.deliveryTime)}`,
              ].filter(Boolean).join(' · ')
              : 'Ngayon na'
          }
          muted={!state.pickupTime && !state.deliveryTime}
          onEdit={() => goTo(STEP.TIMING)}
        />

        <Row
          label="Bayad"
          value={
            <>
              {PAYMENT_COPY[state.paymentMethod]?.label ?? state.paymentMethod}
              {state.paymentMethod !== 'cash_on_delivery' && state.deliveryFeePaid && (
                <span className="block text-body-s text-ink-3">Kasama na ang delivery fee.</span>
              )}
            </>
          }
          onEdit={() => goTo(STEP.PAYMENT)}
        />

        {state.specialInstructions && (
          <Row
            label={FIELDS.notes}
            value={state.specialInstructions}
            onEdit={() => goTo(STEP.PAYMENT)}
          />
        )}
      </section>

      <section className="border border-hairline rounded-card bg-surface-2 px-4 py-3.5" aria-live="polite">
        <h2 className="text-label font-semibold uppercase text-ink-2">{MESSAGES.feeHeading}</h2>

        {/**
          * No rider is an outcome with a way forward, not a dead end.
          *
          * It used to be two sentences and nothing to press: *"walang available na rider"*
          * and *"subukan ulit maya-maya"* — an instruction to leave and come back, on a
          * screen that was already re-asking every thirty seconds by itself. So the
          * customer either sat watching a page that looked stuck, or abandoned a finished
          * order and started a second one to find out.
          *
          * Now it says what is actually happening, and offers the same check *now*. The
          * button is `retry`, not `refresh`: there is no figure on screen to protect, and a
          * retry that shows no sign of running invites a second press.
          */}
        {!!delivery?.unavailable && (
          <>
            <p role="alert" className="mt-1 text-body font-semibold text-ink">{MESSAGES.noRider}</p>
            <p className="mt-1 text-body-s text-ink-3">{MESSAGES.noRiderNote}</p>

            <Button
              type="button"
              variant="ghost"
              size="compact"
              onClick={delivery.retry}
              disabled={delivery.loading}
              className="w-full mt-3"
            >
              {delivery.loading ? MESSAGES.noRiderChecking : MESSAGES.noRiderCheck}
            </Button>
          </>
        )}

        {!delivery?.unavailable && (
          delivery?.quote ? (
            <>
              <p className="mt-1 text-display-l font-display font-bold text-ink tabular-nums">
                {peso(delivery.quote.deliveryFee)}
              </p>

              <p className="mt-1 text-body-s text-ink-3 tabular-nums">
                {delivery.quote.breakdown.distanceKm} km
                {delivery.quote.breakdown.riderDistanceFee > 0
                  && ` · kasama ang ${peso(delivery.quote.breakdown.riderDistanceFee)} papunta sa tindahan`}
              </p>

              {/* Stated once, quietly, and never as a countdown. A ticking clock on a
                  payment screen hurries the one person we deliberately gave time to. */}
              <p className="mt-2 text-body-s text-ink-3">{MESSAGES.feeMayChange}</p>

              {delivery.changed && (
                <p role="status" className="mt-2 text-body-s text-ink border border-hairline rounded-chip px-3 py-2 bg-surface">
                  {MESSAGES.feeChanged} — <span className="tabular-nums">{peso(delivery.changed.from)}</span>
                  {' → '}<span className="font-semibold tabular-nums">{peso(delivery.changed.to)}</span>
                </p>
              )}
            </>
          ) : delivery?.error ? (
            <>
              <p role="alert" className="mt-1 text-body font-semibold text-alert">{MESSAGES.feeFailed}</p>
              <button
                type="button"
                onClick={delivery.retry}
                className="inline-flex items-center min-h-[36px] mt-1 text-body-s font-semibold text-brand
                           transition-colors duration-quick ease-out hover:text-brand-lift"
              >
                {MESSAGES.feeRetry}
              </button>
            </>
          ) : (
            /**
             * **No number at all while the real one is being found.**
             *
             * This showed the base fare here as a placeholder, and that was the bug: a big
             * bold peso figure reads as finished, so the customer takes it as the price and
             * presses the button under it. A number that is about to change is worse than no
             * number — the whole point of this screen is that what is shown is what is
             * charged.
             *
             * The bar is a shape where the figure will be, so the screen does not jump when
             * it lands.
             */
            <>
              <div
                aria-hidden="true"
                className="mt-1 h-9 w-32 rounded-chip bg-hairline/50 animate-pulse motion-reduce:animate-none"
              />
              <p className="mt-2 text-body-s text-ink-2">{MESSAGES.feeLoading}</p>
              <p className="mt-0.5 text-body-s text-ink-3">{MESSAGES.feeLoadingNote}</p>
            </>
          )
        )}
      </section>
    </div>
  )
}
